import { Link } from 'react-router-dom'

import type { Profile } from '@/types/api'

import { experienceDates, initials, tabHref, type ProfileTabKey } from './shared'

/** The profile as a recruiter would read it: nothing editable here. */
export function PreviewTab({ profile }: { profile: Profile }) {
  const name = profile.fullName ?? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
  const locationLine = [profile.location?.city, profile.location?.stateName, profile.location?.countryName]
    .filter(Boolean)
    .join(', ')
  const roleLine = [profile.currentTitle, profile.currentCompany].filter(Boolean).join(' · ')
  const experiences = profile.experiences ?? []
  const links = [
    { label: 'LinkedIn', url: profile.contact?.linkedinUrl },
    { label: 'GitHub', url: profile.contact?.githubUrl },
    { label: 'Website', url: profile.contact?.portfolioUrl },
  ].filter((link): link is { label: string; url: string } => Boolean(link.url))
  const education = [
    profile.educationCourse,
    profile.educationUniversity,
    profile.educationGraduationYear != null ? String(profile.educationGraduationYear) : null,
  ].filter(Boolean)

  return (
    <div className="mx-auto max-w-[760px] space-y-3">
      <p className="label-eyebrow">How recruiters see you</p>

      {/* The document: content width, 16px padding on phones. */}
      <article className="card">
        <header className="flex flex-col gap-4 border-b border-line p-4 sm:flex-row sm:items-center sm:p-6">
          <div
            aria-hidden
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand text-[20px] font-semibold text-oncolor sm:h-16 sm:w-16 sm:text-[22px]"
          >
            {initials(profile)}
          </div>
          <div className="min-w-0">
            <h2 className="break-words text-[22px] font-semibold leading-tight tracking-tight text-ink">
              {name || <Missing tab="profile" anchor="profile-personal" what="your name" />}
            </h2>
            <p className="mt-1 text-[15px] text-ink-soft">
              {profile.headline || roleLine || (
                <Missing tab="profile" anchor="profile-career" what="a headline" />
              )}
            </p>
            {profile.headline && roleLine && profile.headline !== roleLine && (
              <p className="mt-0.5 text-[14px] text-ink-muted">{roleLine}</p>
            )}
            <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13.5px] text-ink-muted">
              {locationLine ? (
                <span>{locationLine}</span>
              ) : (
                <Missing tab="profile" anchor="profile-personal" what="your location" />
              )}
              {profile.totalExperienceYears != null && (
                <span>
                  <span className="num">{profile.totalExperienceYears}</span> years of experience
                </span>
              )}
            </p>
          </div>
        </header>

        <Block title="About">
          {profile.summary?.trim() ? (
            <div className="max-w-[720px] space-y-3 text-[14.5px] leading-relaxed text-ink-soft">
              {profile.summary
                .split(/\n\s*\n/)
                .map((paragraph) => paragraph.trim())
                .filter(Boolean)
                .map((paragraph, index) => (
                  <p key={index} className="whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
            </div>
          ) : (
            <p className="text-[14px] text-ink-faint">
              <Link
                to={tabHref('profile', 'profile-edit-summary')}
                className="text-ink-muted underline underline-offset-4 hover:text-ink"
              >
                Add a summary in the Profile tab
              </Link>
            </p>
          )}
        </Block>

        <Block title="Skills">
          {profile.skills.length ? (
            <ul className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
              {profile.skills.map((skill) => (
                <li
                  key={skill.id}
                  className="flex items-baseline justify-between gap-3 border-b border-line/70 pb-1.5 text-[14px]"
                >
                  <span className="truncate text-ink">
                    {skill.name}
                    {skill.isPrimary && (
                      <span className="ml-1.5 text-[11px] font-medium uppercase tracking-wide text-brand">
                        Primary
                      </span>
                    )}
                  </span>
                  <span className="num shrink-0 text-[13px] text-ink-muted">
                    {skill.yearsOfExperience != null ? `${skill.yearsOfExperience} yrs` : '—'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <Empty tab="skills" />
          )}
        </Block>

        <Block title="Work history">
          {experiences.length ? (
            <ol className="space-y-5">
              {experiences.map((experience) => {
                const dates = experienceDates(experience)
                const bullets = (experience.description ?? '')
                  .split('\n')
                  .map((line) => line.replace(/^[\s•\-*·]+/, '').trim())
                  .filter(Boolean)
                return (
                  <li key={experience.id} className="relative pl-5">
                    <span
                      aria-hidden
                      className="absolute left-0 top-[7px] h-2.5 w-2.5 rounded-full border-2 border-brand bg-surface"
                    />
                    <p className="text-[15px] font-semibold text-ink">{experience.title}</p>
                    <p className="text-[14px] text-ink-soft">{experience.company}</p>
                    <p className="mt-0.5 flex flex-wrap gap-x-3 text-[13px] text-ink-muted">
                      {dates && <span className="num">{dates}</span>}
                      {experience.location && <span>{experience.location}</span>}
                    </p>
                    {bullets.length > 0 && (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-[13.5px] leading-relaxed text-ink-soft">
                        {bullets.map((line, index) => (
                          <li key={index}>{line}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                )
              })}
            </ol>
          ) : (
            <Empty tab="work-history" />
          )}
        </Block>

        <Block title="Education">
          {education.length ? (
            <p className="text-[14.5px] text-ink">{education.join(' · ')}</p>
          ) : (
            <Empty tab="profile" anchor="profile-education" />
          )}
        </Block>

        <Block title="Links" last>
          {links.length ? (
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {links.map((link) => (
                <li key={link.label} className="min-w-0 max-w-full text-[14px]">
                  <span className="text-ink-muted">{link.label}: </span>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-ink underline decoration-line-strong underline-offset-4 hover:decoration-ink"
                  >
                    {link.url.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <Empty tab="profile" anchor="profile-links" />
          )}
        </Block>
      </article>
    </div>
  )
}

function Block({ title, children, last }: { title: string; children: React.ReactNode; last?: boolean }) {
  return (
    <section className={last ? 'p-4 sm:p-6' : 'border-b border-line p-4 sm:p-6'}>
      <h3 className="label-eyebrow mb-3">{title}</h3>
      {children}
    </section>
  )
}

const TAB_NAME: Record<ProfileTabKey, string> = {
  profile: 'Profile',
  skills: 'Skills',
  'work-history': 'Work History',
  preferences: 'Preferences',
  portals: 'Job Portals',
  preview: 'Preview',
  subscription: 'Subscription',
}

function Empty({ tab, anchor }: { tab: ProfileTabKey; anchor?: string }) {
  return (
    <p className="text-[14px] text-ink-faint">
      Nothing here yet.{' '}
      <Link to={tabHref(tab, anchor)} className="text-ink-muted underline underline-offset-4 hover:text-ink">
        Add in the {TAB_NAME[tab]} tab
      </Link>
    </p>
  )
}

function Missing({ tab, anchor, what }: { tab: ProfileTabKey; anchor?: string; what: string }) {
  return (
    <Link
      to={tabHref(tab, anchor)}
      className="font-normal text-ink-faint underline decoration-dashed underline-offset-4 hover:text-ink"
    >
      Add {what}
    </Link>
  )
}
